/* ============================================================
   theme.js — 全站公共脚本
   模块：toast / 滚动进场 reveal / 视频懒加载 / 加购与立即购买 /
         订阅成功提示 / CTA 埋点
   约定：
   - 一律 data-* 属性选择器，不依赖具体 DOM id
   - 不暴露 window 全局函数，跨模块/跨 section 用 CustomEvent：
       theme:toast            detail: { message, duration }
       theme:lazyvideo:refresh  （动态插入视频后请求重新扫描）
       theme:cart:added       detail: { items }（加购成功后广播）
   - 文案不写死在 JS，从 DOM data 属性读取
   ============================================================ */
(function () {
  'use strict';

  /* ── Toast ────────────────────────────────────────────── */
  var toastEl = null;
  var toastTimer = null;

  function showToast(message, duration) {
    var ms = typeof duration === 'number' ? duration : 3500;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'tp-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    clearTimeout(toastTimer);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toastEl.classList.add('tp-toast--show');
        toastTimer = setTimeout(function () {
          toastEl.classList.remove('tp-toast--show');
        }, ms);
      });
    });
  }

  document.addEventListener('theme:toast', function (e) {
    if (e.detail && e.detail.message) showToast(e.detail.message, e.detail.duration);
  });

  /* ── 滚动进场 reveal ──────────────────────────────────── */
  function initReveal() {
    var targets = document.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.07, rootMargin: '0px 0px -18px 0px' }
    );
    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ── 视频懒加载（提前 ~1.5 屏触发） ───────────────────── */
  function initLazyVideo() {
    var videos = document.querySelectorAll('video[data-src]');
    if (!videos.length) return;

    function activate(video) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      video.load();
      if (video.hasAttribute('autoplay')) {
        video.play().catch(function () {});
      }
    }

    if (!('IntersectionObserver' in window)) {
      videos.forEach(activate);
      return;
    }
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          activate(entry.target);
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '300px 0px 300px 0px', threshold: 0 }
    );
    videos.forEach(function (v) {
      io.observe(v);
    });
  }

  document.addEventListener('theme:lazyvideo:refresh', initLazyVideo);

  /* ── 加购 / 立即购买 ──────────────────────────────────── */
  // 触发元素：[data-add-to-cart] 跳 /cart；[data-buy-now] 直达 /checkout。
  // 变体与数量从 data-variant-id / data-quantity 读取。
  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function cartAdd(variantId, quantity) {
    return fetch(routesRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (data.status) throw new Error(data.description || 'cart/add failed');
        return data;
      });
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-add-to-cart], [data-buy-now]');
      if (!btn) return;
      var variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;
      e.preventDefault();

      var quantity = parseInt(btn.dataset.quantity, 10) || 1;
      var destination = btn.hasAttribute('data-buy-now') ? 'checkout' : 'cart';

      btn.disabled = true;
      btn.setAttribute('data-loading', '');
      cartAdd(variantId, quantity)
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('theme:cart:added', { detail: { items: data.items } }));
          window.location.href = routesRoot + destination;
        })
        .catch(function (err) {
          console.error('[theme:cart]', err);
          btn.disabled = false;
          btn.removeAttribute('data-loading');
          var errorMessage = btn.dataset.errorMessage;
          if (errorMessage) showToast(errorMessage);
        });
    },
    true
  );

  /* ── bfcache 复位 ─────────────────────────────────────── */
  // 上面成功分支是整页跳转（→ /cart 或 /checkout），按钮的 loading 态
  // 没有也无法复位——页面已经不在了。浏览器后退时 bfcache 会把整个 DOM
  // 快照原样还原，按钮就卡在 disabled，必须刷新才能再点。
  //
  // 只认 data-loading 这个标记：售罄变体的禁用来自 product-hero 的
  // available 判定（aria-disabled + 无 data-loading），不能被误开。
  // 不判断 event.persisted —— 正常加载时页面上不存在 data-loading，
  // 这段是空转；而部分浏览器不走 bfcache 却也会还原控件状态，一并覆盖。
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('[data-loading]').forEach(function (btn) {
      btn.disabled = false;
      btn.removeAttribute('data-loading');
    });
  });

  /* ── 订阅成功提示 ─────────────────────────────────────── */
  // Shopify 客户订阅表单提交后带 ?customer_posted=true 回跳。
  // 提示文案放在 <body data-subscribed-toast>（locales 提供）。
  (function () {
    if (window.location.search.indexOf('customer_posted=true') === -1) return;
    var message = document.body.getAttribute('data-subscribed-toast');
    if (!message) return;

    var cleanSearch = window.location.search.replace(/[?&]customer_posted=true/, '').replace(/^&/, '?');
    var cleanHash = window.location.hash.replace(/#contact_form$/, '');
    history.replaceState(null, '', window.location.pathname + cleanSearch + cleanHash);

    showToast(message);
  })();

  /* ── CTA 埋点（GA4，可选） ────────────────────────────── */
  // 用法：<a data-track-event="reserve_now_click" data-track-label="Reserve Now">
  document.addEventListener(
    'click',
    function (e) {
      var el = e.target && e.target.closest && e.target.closest('[data-track-event]');
      if (!el) return;
      var payload = {
        event_category: 'CTA',
        event_label: el.dataset.trackLabel || '',
        link_url: el.href || '',
        transport_type: 'beacon'
      };
      if (typeof window.gtag === 'function') {
        window.gtag('event', el.dataset.trackEvent, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: el.dataset.trackEvent }, payload));
      }
    },
    true
  );

  /* ── 平滑滚动到页内锚点 ───────────────────────────────── */
  // 用法：<a data-scroll-to="#some-anchor">
  document.addEventListener(
    'click',
    function (e) {
      var el = e.target && e.target.closest && e.target.closest('[data-scroll-to]');
      if (!el) return;
      var target = document.querySelector(el.dataset.scrollTo);
      if (!target) return;
      e.preventDefault();
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
    },
    true
  );

  /* ── 启动 ─────────────────────────────────────────────── */
  function init() {
    initReveal();
    initLazyVideo();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 主题编辑器里 section 重载后重新扫描
  document.addEventListener('shopify:section:load', init);
})();
